import * as React from 'react';
import { useRef } from 'react';
import { markNotificationAsReaded, deleteNotification } from '../../utils/api';
import { notificationEmitter } from '../../hooks/Notifications/useUserUnreadNotifications';

/**
 * Utils
 */
import { downloadFile } from '../../utils/api';

/**
 * MUI Compoments
 */
import { Button, List, ListItem, ListItemText, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

/**
 * Components
 */
import ConfirmationDialog from '../Dialogs/ConfirmationDialog';

/**
 * Hooks
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useToken from '../../hooks/useToken';

export default function NotificationCard({ notification, onDelete }) {
    const { t } = useTranslation();
    const [readed, setReaded] = useState(!!notification.read_at);
    const [loading, setLoading] = useState(false);
    const { token } = useToken();
    const confirmationDialogRef = useRef(null);

    const handleMarkAsReaded = async (e) => {
        e.preventDefault();

        const controller = new AbortController();
        const signal = controller.signal;
        const uuid = notification.id;

        setLoading(true);

        try {
            const result = await markNotificationAsReaded(token, uuid, signal);
            console.log('Notification marked as read:', result);

            if (result == 'ok') {
                notification.read_at = new Date().toISOString();
                setReaded(true);
                notificationEmitter.emit('force-refresh'); // Emit event to refresh the count
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        const controller = new AbortController();
        const signal = controller.signal;
        const uuid = notification.id;

        setLoading(true);

        try {
            const result = await deleteNotification(token, uuid, signal);
            console.log('Notification deleted:', result);
            if (result == 'ok') {
                if (onDelete && typeof onDelete === 'function') {
                    onDelete(uuid, notification);
                }
            } else {
                console.error('Error deleting notification:', result);
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        } finally {
            setLoading(false);
        }
    };

    const AttachmentsList = () => {
        let hasAttachments = (notification.data?.attachments?.length > 0);
        if (!hasAttachments) {
            return;
        }

        const handleDownload = (url, fileName) => {
            // add /api/ before /notifications/download/
            url = url.replace('/notifications/download/', '/api/notifications/download/');
            console.info('Downloading attachment:', url);
            downloadFile(token, url, fileName);
        };

        return (
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {notification.data.attachments.map((attachment) => (
                    <ListItem
                        key={attachment.uuid}
                        disableGutters
                        secondaryAction={
                            <IconButton onClick={() => { handleDownload(attachment.url, attachment.name) }} aria-label="comment">
                                <CloudDownloadIcon />
                            </IconButton>
                        }
                    >
                        <ListItemText primary={attachment.name} />
                    </ListItem>
                ))}
            </List>
        );
    };

    return (
        <Card sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: '1 0 auto' }}>
                    <Typography component="div" variant="h5">
                        {notification.data.title}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" component="div">
                        {notification.data.body || '?'}
                    </Typography>

                    <AttachmentsList />

                    {notification.data.from_name && (
                        <Typography variant="subtitle2" color="text.secondary" component="div">
                            {t('Sent by')}: {notification.data.from_name || t('unknown')}
                        </Typography>
                    )}
                </CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', pl: 1, pb: 1 }}>
                    {readed ? (
                        <>
                            <CheckCircleIcon color="success" />
                            <Button onClick={() => { confirmationDialogRef.current.openDialog({ title: t('Are you sure you want to delete this notification?'), text: t('Are you sure you want to delete this notification?'), onConfirm: handleDelete }); }}
                                disabled={loading}
                                size="small">{t('Delete')}</Button>
                            <ConfirmationDialog ref={confirmationDialogRef} />
                        </>
                    ) : (
                        <Button onClick={handleMarkAsReaded} disabled={loading} size="small">{t('Mark as readed')}</Button>
                    )}
                </Box>
            </Box>
        </Card>
    );
}